import { BusinessDisasterAppState, BusinessDisasterEligibility } from "src/store";
import { StandardAppConfig } from "src/Components/Configuration/StandardAppConfig";
import { HasMessage } from "../EligibilityPageProjectArchetype";
import { getErrorMessage } from "../Configuration/textConfiguration";
import { get } from "lodash";
import {
    checkYes,
    checkRequired,
    checkAtLeastOneEmployee,
    checkRequiredYesNo,
    PageValidatorContext,
    stringArrayIncludes,
} from "../../Validators";

export const BusinessDisasterValidator = (
    globalState: BusinessDisasterAppState,
    appConfig: StandardAppConfig
): Record<string, HasMessage> => {
    const errorSummary: Record<string, HasMessage> = {};
    const eligibility: BusinessDisasterEligibility = globalState.eligibility;

    const checkIsAbn = (fieldPath: string) => {
        const eligibilityAnswer = get(eligibility, fieldPath)?.toString() ?? "";
        if (!/\d{11}/.test(eligibilityAnswer)) {
            errorSummary[fieldPath] = {
                message: getErrorMessage(
                    appConfig,
                    "eligibilityPage",
                    fieldPath,
                    "EligibilityCriteria",
                    `For [${fieldPath}] you must provide a 11-digit ABN.`
                ),
                errorType: "EligibilityCriteria",
            };
            return false;
        } else {
            return true;
        }
    };

    const checkBusinessDetails = (context: PageValidatorContext) => {
        if (eligibility.hasAbn === "Yes") {
            checkIsAbn("business.abn");
            // If both businessName and BusinessSearch fields are hidden, the error link to businessName points to nothing.
        } else if (!!eligibility.hasAbn) {
            // If eligibility.hasAbn === 'Yes', and business.businessName is falsy, we want the error link to
            //   point to BusinessSearch component - not the invisible businessName Input field.
            //   TODO: implement the ability to manually set ID of node for error link.
            checkRequired(context, "business.businessName");
        }
    };

    const checkInsuranceMustNotBeCovered = (context: PageValidatorContext, fieldPath: string) => {
        if (!checkRequired(context, fieldPath)) {
            return false;
        }

        if (!stringArrayIncludes(["not insured"], get(eligibility, fieldPath))) {
            errorSummary[fieldPath] = {
                message: getErrorMessage(
                    appConfig,
                    "eligibilityPage",
                    fieldPath,
                    "EligibilityCriteria",
                    `For [${fieldPath}] must not fully cover all the costs of repairs to be eligible.`
                ),
                errorType: "EligibilityCriteria",
            };
            return false;
        } else {
            return true;
        }
    };

    const checkNumberOfEmployees = (context: PageValidatorContext, fieldPath: string) => {
        checkAtLeastOneEmployee(context, fieldPath);
        const value = get(context.formObject, fieldPath);
        if (parseInt(value ?? "") >= (appConfig.maxEmployees || 20)) {
            errorSummary[fieldPath] = {
                message: getErrorMessage(
                    appConfig,
                    "eligibilityPage",
                    fieldPath,
                    "Rule",
                    `[${fieldPath}] is more than the maximum number of employees (${appConfig.maxEmployees || 20}).`
                ),
                errorType: "Rule",
            };
        }
    };

    const context: PageValidatorContext = {
        appConfig: appConfig,
        formObject: eligibility,
        errorSummary: errorSummary,
        pageName: "eligibilityPage",
    };

    checkRequiredYesNo(context, "hasAbn");
    checkBusinessDetails(context);
    checkYes(context, "directDamages");
    checkYes(context, "intendToReEstablish");
    checkYes(context, "operatedDuringDisaster");
    checkInsuranceMustNotBeCovered(context, "insurance");

    checkRequiredYesNo(context, "hasFullTimeEmployees");
    if (eligibility.hasFullTimeEmployees === "Yes") {
        checkNumberOfEmployees(context, "employees");
    } else if (eligibility.hasFullTimeEmployees === "No") {
        checkRequiredYesNo(context, "soleTrader50PercentIncome");
    }

    return errorSummary;
};
